/**
 * VendorBrandBadge — square, brand-coloured tile used wherever the UI needs
 * to render a vendor's mark inside a header or hero card (currently:
 * ``<ConnectAccountDialog>`` body header, ``<SpreadsheetConnectDialog>``
 * source-mode header).
 *
 * Centralises three rules that used to live as 14-line inline blocks:
 *
 *   1. Brand colour resolution — ``vendorBrandColor(vendorId)``: when the
 *      vendor ships an official accent we paint an inline-style background
 *      at 10% alpha + foreground at the brand hex (Tailwind can't synth
 *      per-vendor hex at build time). Otherwise we fall back to the
 *      Ando orange tokens so the design system stays respected.
 *
 *   2. Icon resolution — ``vendorIcon(vendorId)`` first, fallback to the
 *      caller-provided ``fallbackIcon`` (typically the capability lucide
 *      stand-in from the spec). Keeps single-vendor specs working without
 *      registering a brand SVG.
 *
 *   3. Size + a11y — two presets (``md`` 40px / ``lg`` 56px) with matching
 *      inner glyph sizes (20 / 28), ``aria-hidden`` and flex-centred.
 */

import { cn } from "@ando/ui";
import type * as React from "react";

import { vendorBrandColor, vendorIcon } from "./vendorIcons";

export type VendorBrandBadgeSize = "md" | "lg";

export interface VendorBrandBadgeProps {
  /** Vendor slug (e.g. ``"gmail"``, ``"sap_odata"``) — drives the icon + color lookup. */
  readonly vendorId: string;
  /** Rendered when no brand SVG is registered for ``vendorId``. */
  readonly fallbackIcon: React.ReactNode;
  /** ``"lg"`` (default) → 56px tile / 28px glyph; ``"md"`` → 40px / 20px. */
  readonly size?: VendorBrandBadgeSize;
  /** Optional className override (extra spacing, ring on focus, …). */
  readonly className?: string;
}

const TILE_CLS: Readonly<Record<VendorBrandBadgeSize, string>> = {
  md: "h-10 w-10",
  lg: "h-14 w-14",
};

const GLYPH_CLS: Readonly<Record<VendorBrandBadgeSize, string>> = {
  md: "h-5 w-5",
  lg: "h-7 w-7",
};

export const VendorBrandBadge: React.FC<VendorBrandBadgeProps> = ({
  vendorId,
  fallbackIcon,
  size = "lg",
  className,
}) => {
  const brandColor = vendorBrandColor(vendorId);
  const brandIcon = vendorIcon(vendorId, GLYPH_CLS[size]);
  const glyph = brandIcon ?? fallbackIcon;
  const tileBase = cn(
    "flex shrink-0 items-center justify-center rounded-xl",
    TILE_CLS[size],
    className,
  );

  if (brandColor !== null) {
    return (
      <div
        aria-hidden
        className={tileBase}
        style={{ background: `${brandColor}1A`, color: brandColor }}
      >
        {glyph}
      </div>
    );
  }

  return (
    <div aria-hidden className={cn(tileBase, "bg-ando-orange-soft text-ando-orange")}>
      {glyph}
    </div>
  );
};
VendorBrandBadge.displayName = "VendorBrandBadge";
